/**
 * The password a new account starts with.
 *
 * Every account used to open with the same pre-filled string. An
 * administrator who did not notice the field created accounts whose password
 * only the source code knew, and the staff were left unable to sign in — so
 * each account now gets its own, and the administrator is shown it once, to
 * hand over.
 *
 * Shop-floor staff read these off a slip and type them on a tablet, so the
 * shape is two short words and four digits rather than a run of random
 * characters. That is weak against an attacker who can guess at leisure, and
 * it is meant to be: it is a handover credential the holder changes, and a
 * password nobody can type is one that ends up written on the machine.
 */

/** Short, unambiguous when spoken aloud, and free of workshop jargon that might read as a real code. */
const WORDS = [
  "Amber",
  "Anvil",
  "Basin",
  "Cedar",
  "Copper",
  "Delta",
  "Ember",
  "Flint",
  "Garnet",
  "Harbor",
  "Indigo",
  "Jasper",
  "Kettle",
  "Lantern",
  "Marble",
  "Nickel",
  "Onyx",
  "Pewter",
  "Quartz",
  "Ripple",
  "Saffron",
  "Timber",
  "Umber",
  "Violet",
  "Walnut",
  "Yarrow",
  "Zephyr",
  "Beacon",
  "Cobalt",
  "Dunes",
  "Elmwood",
  "Fathom",
] as const;

/** Crypto-backed so two administrators working at once cannot collide. */
function randomInts(count: number, ceiling: number): number[] {
  const source = globalThis.crypto;
  if (source?.getRandomValues) {
    const buf = new Uint32Array(count);
    source.getRandomValues(buf);
    // Rejectionless modulo is fine here: the bias is far below what matters
    // for a credential that is handed over and then changed.
    return Array.from(buf, (n) => n % ceiling);
  }
  // Only reachable in an environment without Web Crypto; still unpredictable
  // enough that two accounts created in one sitting differ.
  return Array.from({ length: count }, () => Math.floor(Math.random() * ceiling));
}

/**
 * A fresh initial password, e.g. `Cedar-Quartz-4821`.
 *
 * Always satisfies the account rules the forms enforce: at least eight
 * characters, with a letter and a digit.
 */
export function generateInitialPassword(): string {
  const [firstWord, secondWord, digits] = randomInts(3, 1_000_000);
  const first = WORDS[firstWord % WORDS.length];
  let second = WORDS[secondWord % WORDS.length];
  // Two of the same word reads like a mistake and halves the choice.
  if (second === first) second = WORDS[(secondWord + 1) % WORDS.length];
  const tail = String(digits % 10_000).padStart(4, "0");
  return `${first}-${second}-${tail}`;
}

/** The rule both the create and reset forms hold a password to. */
export function describePasswordRule(): string {
  return "At least 8 characters, including a letter and a digit";
}

export function isPasswordAcceptable(password: string): boolean {
  return password.length >= 8 && /[A-Za-z]/.test(password) && /\d/.test(password);
}
